/* R32 product discovery. No order, inventory, discount, membership or price writes.
   Cart additions only follow an explicit submit. POST failures are never retried. */
(() => {
 'use strict';
 if (customElements.get('mm-product-discovery')) return;
 const $ = (root, selector) => root.querySelector(selector);
 const $$ = (root, selector) => [...root.querySelectorAll(selector)];
 const text = (root, selector) => $(root, selector)?.textContent.trim() || '';
 const cardId = card => card.dataset.productId;
 class ProductDiscovery extends HTMLElement {
  connectedCallback() {
   if (this.ready) return;
   let labels;
   try { labels = JSON.parse($(this, '[data-reco-strings]')?.textContent || ''); }
   catch (_) {
    // A previously registered element can connect before the HTML parser adds its children.
    if (!this.waiting) {
     this.waiting = new MutationObserver(() => this.connectedCallback());
     this.waiting.observe(this, {childList:true, subtree:true, characterData:true});
    }
    return;
   }
   this.waiting?.disconnect(); this.waiting = null;
   this.ready = true;
   this.labels = labels;
   this.touched = false;
   this.expanded = false;
   this.selection = new Map();
   this.pending = new Set();
   this.controllers = new Set();
   this.originalLabels = new WeakMap();
   this.shell = $(this, '[data-reco-shell]');
   this.dialog = $(this, '[data-reco-dialog]');
   this.compareTrigger = null;
   this.onDialogClose = () => {
    const target = this.compareTrigger;
    this.compareTrigger = null;
    if (target?.isConnected && typeof target.focus === 'function') target.focus({preventScroll:true});
   };
   this.maximum = Math.min(12, Math.max(4, Number(this.dataset.maximum) || 8));
   this.onClick = event => this.click(event);
   this.onChange = event => this.change(event);
   this.onSubmit = event => this.add(event);
   this.freeze = () => { this.touched = true; };
   this.addEventListener('click', this.onClick);
   this.addEventListener('change', this.onChange);
   this.addEventListener('submit', this.onSubmit);
   this.addEventListener('pointerdown', this.freeze);
   this.addEventListener('focusin', this.freeze);
   this.onResize = () => this.scrollControls();
   window.addEventListener('resize', this.onResize, {passive:true});
   this.onCart = event => {
    const cart = event.detail?.cart;
    if (cart && Array.isArray(cart.items)) this.reflectCart(cart);
   };
   document.addEventListener('cart:updated', this.onCart);
   this.dialog?.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
   this.dialog?.addEventListener('close', this.onDialogClose);
   this.initializeCards();
   this.refresh();
   if ('ResizeObserver' in window) {
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this);
   }
   const load = () => { this.observer?.disconnect(); this.load(); };
   // An empty shelf has almost no layout height. On short product pages that can
   // make a lazy IntersectionObserver unreliable, so populate the bottom fallback
   // immediately when no server-rendered recommendation cards are present.
   const hasInitialCards = Boolean(this.querySelector('[data-mm-reco-card]'));
   if (!hasInitialCards) load();
   else if ('IntersectionObserver' in window) {
    this.observer = new IntersectionObserver(entries => {
     if (entries.some(entry => entry.isIntersecting)) load();
    }, {rootMargin:'700px'});
    this.observer.observe(this);
   } else load();
  }
  disconnectedCallback() {
   this.waiting?.disconnect(); this.waiting = null;
   this.observer?.disconnect(); this.resizeObserver?.disconnect();
   this.controllers?.forEach(controller => controller.abort());
   this.removeEventListener('click', this.onClick);
   this.removeEventListener('change', this.onChange);
   this.removeEventListener('submit', this.onSubmit);
   this.removeEventListener('pointerdown', this.freeze);
   this.removeEventListener('focusin', this.freeze);
   window.removeEventListener('resize', this.onResize);
   document.removeEventListener('cart:updated', this.onCart);
   this.dialog?.removeEventListener('close', this.onDialogClose);
   this.ready = false;
  }
  initializeCards() {
   $$(this, '[data-mm-reco-card]:not([data-reco-ready])').forEach(card => {
    card.dataset.recoReady = 'true';
    const compare = $(card, '[data-reco-compare-label]');
    if (compare && typeof this.dialog?.showModal === 'function') compare.hidden = false;
    const input = $(card, '[data-reco-compare]');
    if (input) input.checked = this.selection.has(cardId(card));
   });
   $$(this, '[data-reco-grid]:not([data-reco-scroll-ready])').forEach(grid => {
    grid.dataset.recoScrollReady = 'true';
    grid.addEventListener('scroll', () => this.scrollControls(), {passive:true});
   });
  }
  refresh() {
   let total = 0;
   $$(this, '[data-reco-group]').forEach(group => {
    const mode = group.dataset.recoGroup;
    const grid = $(group, '[data-reco-grid]');
    const cards = $$(grid, '[data-mm-reco-card]');
    group.hidden = !cards.length; total += cards.length;
    const count = $(group, '[data-reco-count]');
    if (count) count.textContent = `${cards.length} ${this.labels.finds}`;
    cards.forEach((card, index) => { card.hidden = mode === 'related' && !this.expanded && index >= 4; });
    const more = $(group, '[data-reco-more]');
    if (more) {
     more.hidden = cards.length <= 4;
     more.textContent = this.expanded ? this.labels.less : this.labels.more;
     more.setAttribute('aria-expanded', String(this.expanded));
    }
   });
   this.shell.hidden = !total;
   this.scrollControls();
  }
  scrollControls() {
   $$(this, '[data-reco-group]').forEach(group => {
    const grid = $(group, '[data-reco-grid]');
    const controls = $(group, '[data-reco-scroll-buttons]');
    if (!grid || !controls) return;
    const overflow = grid.scrollWidth - grid.clientWidth;
    controls.hidden = group.hidden || overflow < 8;
    const prev = $(controls, '[data-reco-scroll="-1"]');
    const next = $(controls, '[data-reco-scroll="1"]');
    if (prev) prev.disabled = grid.scrollLeft <= 3;
    if (next) next.disabled = grid.scrollLeft >= overflow - 3;
   });
  }
  url(path) {
   const url = new URL(path, location.origin);
   if (url.origin !== location.origin) throw new Error('Same-origin URL required');
   return url;
  }
  async read(url, milliseconds = 8000, format = 'text') {
   const controller = new AbortController();
   this.controllers.add(controller);
   const timer = setTimeout(() => controller.abort(), milliseconds);
   try {
    const response = await fetch(url, {credentials:'same-origin', cache:'no-store', signal:controller.signal});
    const data = format === 'json' ? await response.json() : await response.text();
    return {ok:response.ok, status:response.status, data};
   } finally { clearTimeout(timer); this.controllers.delete(controller); }
  }
  async load() {
   if (this.loaded) return;
   this.loaded = true;
   const intents = [];
   if (this.dataset.manualOnly !== 'true') intents.push('related');
   if (Number(this.dataset.hasPairings) > 0) intents.push('complementary');
   await Promise.all(intents.map(async intent => {
    try {
     const url = this.url(this.dataset.endpoint);
     url.searchParams.set('product_id', this.dataset.productId);
     url.searchParams.set('section_id', this.dataset.sectionId);
     url.searchParams.set('limit', '10');
     url.searchParams.set('intent', intent);
     const params = new URL(location.href).searchParams;
     let preview = params.get('preview_theme_id');
     if (!preview && window.Shopify?.theme?.role !== 'main') preview = String(window.Shopify?.theme?.id || '');
     if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
     const response = await this.read(url);
     if (!response.ok || !this.isConnected) return;
     const html = new DOMParser().parseFromString(response.data, 'text/html');
     const incoming = $$(html, 'mm-product-discovery').find(node => node.dataset.build === this.dataset.build && node.dataset.productId === this.dataset.productId && node.dataset.responseIntent === intent);
     if (!incoming || !this.isConnected) return;
     this.merge(incoming, intent);
    } catch (_) { /* The crawlable server-rendered fallback remains. */ }
   }));
  }
  merge(incoming, intent) {
   const grid = $(this, `[data-reco-grid="${intent}"]`);
   const source = $(incoming, `[data-reco-grid="${intent}"]`);
   if (!grid || !source) return;
   const current = $$(grid, '[data-mm-reco-card]');
   const existingOther = new Set($$(this, '[data-mm-reco-card]').filter(card => !grid.contains(card)).map(cardId));
   const byId = new Map(current.map(card => [cardId(card), card]));
   let added = false;
   $$(source, '[data-mm-reco-card]').forEach(card => {
    const id = cardId(card);
    if (!/^\d+$/.test(id || '') || id === this.dataset.productId || existingOther.has(id)) return;
    if (!byId.has(id)) { byId.set(id, document.importNode(card, true)); added = true; }
   });
   if (!added) return;
   let ordered = [...byId.values()];
   // Improve ranking before the shelf reaches the viewport; never shuffle a shelf being used.
   if (!this.touched && this.getBoundingClientRect().top > window.innerHeight + 40) {
    ordered.sort((a,b) => Number(b.dataset.score) - Number(a.dataset.score));
   }
   const maximum = intent === 'related' ? this.maximum : 4;
   const keep = ordered.slice(0, maximum);
   // Avoid replacing focused controls, pending purchases, or selected comparison cards.
   if (this.touched || this.pending.size) {
    const ids = new Set(current.map(cardId));
    keep.filter(card => !ids.has(cardId(card))).forEach(card => {
     if ($$(grid, '[data-mm-reco-card]').length < maximum) grid.append(card);
    });
   } else grid.replaceChildren(...keep);
   this.initializeCards(); this.refresh();
   // Shared MM saved-products uses this existing section lifecycle hook to sync new controls.
   this.dispatchEvent(new CustomEvent('shopify:section:load', {bubbles:true, detail:{sectionId:this.dataset.sectionId, source:'r32-product-discovery'}}));
  }
  click(event) {
   if (!(event.target instanceof Element)) return;
   const button = event.target.closest('button');
   if (!button || !this.contains(button)) return;
   if (button.matches('[data-reco-more]')) {
    this.expanded = !this.expanded;
    this.refresh();
    const status = $(this, '[data-reco-more-status]');
    if (status) status.textContent = this.expanded ? this.labels.more : this.labels.less;
   } else if (button.matches('[data-reco-scroll]')) {
    const grid = $(button.closest('[data-reco-group]'), '[data-reco-grid]');
    const card = $$(grid, '[data-mm-reco-card]').find(node => !node.hidden);
    const distance = (card?.getBoundingClientRect().width || grid.clientWidth * .85) + 12;
    grid.scrollBy({left:Number(button.dataset.recoScroll) * distance, behavior:matchMedia('(prefers-reduced-motion:reduce)').matches ? 'auto' : 'smooth'});
   } else if (button.matches('[data-reco-open-compare]')) { this.compareTrigger = button; this.compare(); }
   else if (button.matches('[data-reco-close-compare]')) this.dialog?.close();
   else if (button.matches('[data-reco-clear-compare]')) {
    this.selection.clear(); $$(this, '[data-reco-compare]').forEach(input => input.checked = false); this.updateSelection();
    $(this, '[data-reco-compare]')?.focus({preventScroll:true});
   }
  }
  change(event) {
   const input = event.target;
   if (!input.matches?.('[data-reco-compare]')) return;
   const card = input.closest('[data-mm-reco-card]');
   const id = cardId(card);
   if (input.checked && this.selection.size >= 3) {
    input.checked = false;
    $(this, '[data-reco-selection]').textContent = this.labels.limit;
    return;
   }
   if (input.checked) this.selection.set(id, card); else this.selection.delete(id);
   this.updateSelection();
  }
  updateSelection() {
   const bar = $(this, '[data-reco-comparebar]');
   bar.hidden = this.selection.size === 0;
   $(bar, '[data-reco-selection]').textContent = `${this.labels.selected}: ${this.selection.size}/3`;
   $(bar, '[data-reco-open-compare]').disabled = this.selection.size < 2;
  }
  compare() {
   if (this.selection.size < 2 || typeof this.dialog?.showModal !== 'function') return;
   const products = [...this.selection.values()].map(card => ({
    url:$(card, '[data-reco-title]').href,
    title:text(card, '[data-reco-title]'),
    image:$(card, '.mm-reco-image-link img')?.currentSrc || $(card, '.mm-reco-image-link img')?.src,
    ...Object.fromEntries(['brand','scale','condition','casting','part','kind','price'].map(key => [key, text(card, `[data-reco-${key}]`)])),
    priceNote:text(card, '[data-reco-price-note]')
   }));
   const table = document.createElement('table');
   const caption = document.createElement('caption'); caption.className = 'mm-reco-sr'; caption.textContent = this.labels.selected; table.append(caption);
   const head = table.createTHead().insertRow();
   const corner = document.createElement('td'); head.append(corner);
   products.forEach(product => {
    const th = document.createElement('th'); th.scope = 'col';
    if (product.image) { const img = document.createElement('img'); img.src = product.image; img.alt = ''; img.width = 240; img.height = 160; th.append(img); }
    const link = document.createElement('a'); link.href = product.url; link.textContent = product.title; th.append(link); head.append(th);
   });
   const body = table.createTBody();
   ['price','brand','kind','scale','casting','condition','part'].forEach(key => {
    if (!products.some(product => product[key])) return;
    const row = body.insertRow(); const label = document.createElement('th'); label.scope = 'row'; label.textContent = this.labels[key]; row.append(label);
    products.forEach(product => {
     const cell = row.insertCell(); cell.textContent = product[key] || this.labels.unspecified;
     if (key === 'price' && product.priceNote) { const note = document.createElement('small'); note.style.display = 'block'; note.textContent = product.priceNote; cell.append(note); }
    });
   });
   const row = body.insertRow(); row.insertCell(); products.forEach(product => {
    const link = document.createElement('a'); link.href = product.url; link.textContent = this.labels.details + ' →'; row.insertCell().append(link);
   });
   $(this, '[data-reco-table]').replaceChildren(table);
   this.dialog.showModal(); $(this.dialog, '[data-reco-close-compare]')?.focus();
  }
  status(card, message, error = false, linkToCart = false) {
   const status = $(card, '[data-reco-card-status]');
   status.replaceChildren(document.createTextNode(message));
   status.hidden = false; status.toggleAttribute('data-error', error);
   if (linkToCart) { const link = document.createElement('a'); link.href = this.dataset.cartUrl; link.textContent = this.labels.cart + ' →'; status.append(link); }
  }
  reflectCart(cart) {
   if (!Array.isArray(cart?.items)) return;
   const ids = new Set(cart.items.map(item => String(item.product_id)));
   $$(this, '[data-mm-reco-card]').forEach(card => {
    const button = $(card, '[data-reco-submit]');
    if (!button) return;
    if (ids.has(cardId(card))) {
     if (!this.originalLabels.has(button)) this.originalLabels.set(button, $(button, 'span')?.textContent || button.textContent);
     button.disabled = true; button.dataset.recoAdded = 'true';
     const label = $(button, 'span'); if (label) label.textContent = this.labels.in_cart;
    } else if (button.hasAttribute('data-reco-added') && !this.pending.has(cardId(card))) {
     button.disabled = false; delete button.dataset.recoAdded;
     const label = $(button, 'span'); if (label) label.textContent = this.originalLabels.get(button) || this.labels.details;
     const status = $(card, '[data-reco-card-status]'); if (status) status.hidden = true;
    }
   });
  }
  async add(event) {
   const form = event.target;
   if (!form.matches?.('form[data-mm-reco-add]') || event.defaultPrevented || !window.fetch) return;
   event.preventDefault(); event.stopPropagation(); // Only this card form; prevent another delegated handler from adding it twice.
   const card = form.closest('[data-mm-reco-card]');
   const id = cardId(card); const button = $(form, '[data-reco-submit]');
   if (this.pending.has(id) || button.disabled) return;
   const label = $(button, 'span'); const original = label?.textContent || button.textContent;
   this.originalLabels.set(button, original);
   this.pending.add(id); button.disabled = true; button.setAttribute('aria-busy','true'); if (label) label.textContent = this.labels.adding;
   const controller = new AbortController(); this.controllers.add(controller);
   const timer = setTimeout(() => controller.abort(), 12000);
   let confirmed = false;
   try {
    const endpoint = this.url(this.dataset.addUrl + '.js');
    const response = await fetch(endpoint, {method:'POST',credentials:'same-origin',headers:{Accept:'application/json'},body:new FormData(form),signal:controller.signal});
    let data; try { data = await response.json(); } catch (_) { throw new Error('Unknown cart state'); }
    if (!response.ok) {
     if (response.status >= 400 && response.status < 500) {
      this.status(card, typeof data.description === 'string' ? data.description : this.labels.error, true, response.status === 422);
      button.disabled = false; if (label) label.textContent = original;
      return;
     }
     throw new Error('Unknown cart state');
    }
    confirmed = true; button.dataset.recoAdded = 'true'; if (label) label.textContent = this.labels.added;
    this.status(card, this.labels.added, false, true);
    try {
     const cartResponse = await this.read(this.url(this.dataset.cartUrl + '.js'),6000,'json');
     if (cartResponse.ok) {
      const cart = cartResponse.data;
      if (Number.isSafeInteger(cart.item_count)) {
       $$ (document, '[data-r32-cart-count],[data-lc-cart-count]').forEach(node => node.textContent = String(cart.item_count));
      }
      this.reflectCart(cart);
      document.dispatchEvent(new CustomEvent('cart:updated',{detail:{cart,source:'r32-product-discovery'}}));
     }
    } catch (_) { /* POST succeeded. Do not falsely report a failed add or retry it. */ }
   } catch (_) {
    if (!confirmed) { if (label) label.textContent = this.labels.cart; this.status(card,this.labels.uncertain,true,true); }
    // On an ambiguous network failure the button stays disabled: check cart before retrying.
   } finally {
    clearTimeout(timer); this.controllers.delete(controller); this.pending.delete(id); button.removeAttribute('aria-busy');
   }
  }
 }
 customElements.define('mm-product-discovery', ProductDiscovery);
})();
